import type { MatterFieldWidget } from '@common/utils/matterFields';
import { InputAdornment, TextField } from '@mui/material';
import { AdapterLuxon } from '@mui/x-date-pickers/AdapterLuxon';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DateTime } from 'luxon';
import { useState } from 'react';
import CurrencyInputIcon from '@/components/CurrencyInputIcon';
import GooglePlacesAutocomplete from '@/components/GooglePlacesAutocomplete';
import { VALID_CURRENCY_INPUT } from '@/constants';

interface Props {
    name: string;
    widget: MatterFieldWidget;
    /** ISO date for the date widget, a plain number for currency. */
    value: string;
    error?: boolean;
    size?: 'small' | 'medium';
    onChange(value: string): void;
}

function toIsoDateValue(date: DateTime | null): string {
    return date?.isValid ? (date.toISODate() ?? '') : '';
}

export default function MatterFieldInput({
    name,
    widget,
    value,
    error = false,
    size,
    onChange,
}: Props) {
    // A partly typed date is sent as '', so keep it while the value still matches.
    const [pickedDate, setPickedDate] = useState<DateTime | null>(null);
    const parsedDate = value ? DateTime.fromISO(value) : null;
    const dateValue =
        pickedDate && toIsoDateValue(pickedDate) === value
            ? pickedDate
            : parsedDate?.isValid
              ? parsedDate
              : null;

    if (widget === 'address') {
        return (
            <GooglePlacesAutocomplete
                name={name}
                value={value}
                isError={error}
                onChange={onChange}
            />
        );
    }

    if (widget === 'date') {
        return (
            <LocalizationProvider dateAdapter={AdapterLuxon}>
                <DatePicker
                    value={dateValue}
                    onChange={(newValue) => {
                        setPickedDate(newValue);
                        onChange(toIsoDateValue(newValue));
                    }}
                    slotProps={{ textField: { error, fullWidth: true, size } }}
                    sx={{ width: '100%' }}
                />
            </LocalizationProvider>
        );
    }

    if (widget === 'currency') {
        return (
            <TextField
                type="text"
                fullWidth
                size={size}
                value={value}
                error={error}
                onChange={(event) => {
                    const nextValue = event.target.value;
                    if (VALID_CURRENCY_INPUT.test(nextValue)) {
                        onChange(nextValue);
                    }
                }}
                inputProps={{
                    pattern: VALID_CURRENCY_INPUT,
                    inputMode: 'decimal',
                }}
                InputProps={{
                    startAdornment: (
                        <InputAdornment position="start">
                            <CurrencyInputIcon />
                        </InputAdornment>
                    ),
                }}
            />
        );
    }

    return (
        <TextField
            type="text"
            fullWidth
            size={size}
            value={value}
            error={error}
            onChange={(event) => onChange(event.target.value)}
        />
    );
}
